// 공통 탭 컨트롤 (회색 트랙 + 흰 pill).
//
// 프레젠테이션만 갖는다 — **상태도 라우터도 모른다.** 값이 index 인지 문자열인지,
// 바뀔 때 setState 를 하는지 router.push 를 하는지는 전부 호출부 몫이다.
// WorkSNB(index) · 게시판 폼(formType) · 캘린더(URL의 month/week) · 전자결재(subtab)가
// 같은 UI를 쓰면서 값만 다르기 때문에 이 선에서 잘랐다.

import React, { useEffect, useRef, useState } from 'react';

import Spinner, { SpinnerProps } from '@/components/@IntranetLibrary/Spinner';
import { overCountText } from '@/utils';
import { Box } from '@mui/material';

import { getSelectedIndex } from './select';
import {
  SelectedTabIndicator,
  Tab as TabStyle,
  TabContent,
  TabCount,
  Tabs as TabsStyle,
  TabTitle,
  TabWrapper,
  resolveContentTabSize,
  type ContentTabSize,
  type ContentTabSizeName,
  type ContentTabSizeSpec,
} from './styled';

export type { ContentTabSize, ContentTabSizeName, ContentTabSizeSpec };

export type ContentTabItem<T> = {
  readonly value: T;
  readonly title: string;
  readonly count?: number;
  readonly isLoading?: boolean;
  readonly icon?: React.ReactNode;
};

export type ContentTabProps<T> = {
  readonly items: readonly ContentTabItem<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** 트랙과 각 탭이 부모 폭을 채운다 (2분할 스위치 형태) */
  readonly fullWidth?: boolean;
  /** 프리셋 이름(`'small' | 'regular' | 'medium'`), 또는 맞는 게 없으면 값을 직접 */
  readonly size?: ContentTabSize;
  readonly spinnerColor?: SpinnerProps['theme'];
};

export default function ContentTab<T>({
  items,
  value,
  onChange,
  fullWidth = false,
  size = 'small',
  spinnerColor = 'black',
}: ContentTabProps<T>) {
  const sizeSpec = resolveContentTabSize(size);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const selectedIndex = getSelectedIndex(items, value);

  useEffect(() => {
    // 값이 목록에 없으면(-1) 인디케이터를 그대로 둔다 — 옮길 자리가 없다
    const tab = selectedIndex >= 0 ? tabRefs.current[selectedIndex] : null;
    if (!tab) return;

    const { offsetLeft, offsetWidth } = tab;
    // 레이아웃이 확정된 다음 프레임에 읽어야 폭이 0으로 잡히지 않는다
    const timer = setTimeout(() => {
      setIndicatorStyle({ left: offsetLeft + 4, width: offsetWidth });
    }, 1);
    return () => clearTimeout(timer);
  }, [selectedIndex, items]);

  return (
    <TabWrapper isFullWidth={fullWidth}>
      <SelectedTabIndicator
        layout
        style={indicatorStyle}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      />
      <TabsStyle
        isFullWidth={fullWidth}
        // MUI 는 Tab 에 value 를 안 주면 index 를 값으로 쓴다.
        // 선택된 게 없을 때 `false` 를 줘야 MUI 경고가 안 뜬다
        value={selectedIndex >= 0 ? selectedIndex : false}
        onChange={(_, index: number) => {
          const next = items[index];
          if (next) onChange(next.value);
        }}
      >
        {items.map(({ title, count, isLoading, icon }, index) => (
          <TabStyle
            disableRipple
            key={index}
            isFullWidth={fullWidth}
            sizeSpec={sizeSpec}
            label={
              <TabContent>
                {icon && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {icon}
                  </Box>
                )}
                <TabTitle
                  sx={{
                    color: selectedIndex === index ? '#4D5157' : '#888D96',
                  }}
                >
                  {title}
                </TabTitle>
                {isLoading ? (
                  <Spinner theme={spinnerColor} size={'10px'} />
                ) : (
                  !!count && (
                    <TabCount
                      sx={{ fontWeight: selectedIndex === index ? 700 : 400 }}
                    >
                      {overCountText(count)}
                    </TabCount>
                  )
                )}
              </TabContent>
            }
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
          />
        ))}
      </TabsStyle>
    </TabWrapper>
  );
}
